import { Card, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import CustomTable from "../../components/CustomTable/CustomTable";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddPresentation from "./AddPresentation";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";

const Presentation = () => {
  const [showAddLeadershipCouncil, setShowAddLeadershipCouncil] =
    useState(false);
  const [leadershipCouncils, setLeadershipCouncils] = useState<any>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadershipCouncilToDelete, setLeadershipCouncilToDelete] = useState<
    string | null
  >(null);
  const [leadershipCouncilToEdit, setLeadershipCouncilToEdit] = useState<
    any | null
  >(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");
  const [sortField, setSortField] = useState<string>("publishedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAtFormatted");
  const [loading, setLoading] = useState<boolean>(false);
  const sortFieldMap: Record<string, string> = {
    createdAtFormatted: "publishedAt",
    title: "title",
    description: "description",
    status: "status",
  };
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  useEffect(() => {
    getMediaTags(searchRef.current);
  }, [currentPage, sortField, sortOrder]);

  const getMediaTags = (keyword = "") => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    // Status filter
    if (search && search !== "ALL") {
      params.append("status", search);
    }

    // Keyword search
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    // Sorting
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);
    WebService.getAPI({
      action: `resources/get-resources-admin?resource_type=PRESENTATION&${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        const formattedRows = res?.data?.data.map((item: any) => {
          // ✅ strip HTML
          const stripHtml = (html: string) => {
            if (!html) return "";
            const doc = new DOMParser().parseFromString(html, "text/html");
            return doc.body.textContent || "";
          };

          // ✅ truncate to N words
          const truncateDescription = (text: string, wordLimit = 4) => {
            if (!text) return "";
            const words = text.split(/\s+/);
            return words.length > wordLimit
              ? words.slice(0, wordLimit).join(" ") + "..."
              : text;
          };

          const plainTextDescription = stripHtml(item.description);
          const truncated = truncateDescription(plainTextDescription, 4);

          return {
            ...item,
            description: truncated,
            favourite: (
              <>
                {perm.canEdit && (
                  <span
                    className="cursor-pointer"
                    onClick={() =>
                      toggleFavourite(item._id, item.show_favorite)
                    }
                    style={{
                      fontSize: "20px",
                      color: item.show_favorite ? "gold" : "#ccc",
                      transition: "0.2s",
                    }}
                  >
                    ★
                  </span>
                )}
              </>
            ),
            action: (
              <div>
                {perm.canEdit && (
                  <PencilFill
                    className="me-3 cursor-pointer"
                    onClick={() => {
                      setShowAddLeadershipCouncil(true);
                      setLeadershipCouncilToEdit(item);
                    }}
                    title="Edit"
                  />
                )}
                {perm.canDelete && (
                  <TrashFill
                    color="#DC143C"
                    className="cursor-pointer"
                    onClick={() => handleDeleteEvent(item._id)}
                    title="Delete"
                  />
                )}
              </div>
            ),
          };
        });
        setLeadershipCouncils(formattedRows);
        setTotalCount(res?.data?.pagination?.total);
      })
      .catch((err: any) => {});
  };
  const toggleFavourite = async (id: any, currentValue: any) => {
    try {
      await WebService.putAPI({
        action: `resources/toggleFavourites/${id}`,
        body: { show_favorite: !currentValue },
      });

      const updated = leadershipCouncils.map((row: any) => {
        if (row._id === id) {
          const newVal = !currentValue;

          return {
            ...row,
            show_favorite: newVal,
            favourite: (
              <span
                className="cursor-pointer"
                onClick={() => toggleFavourite(id, newVal)}
                style={{
                  fontSize: "20px",
                  color: newVal ? "gold" : "#ccc",
                  transition: "0.2s",
                }}
              >
                ★
              </span>
            ),
          };
        }
        return row;
      });

      setLeadershipCouncils(updated);
      getMediaTags(searchRef.current);
    } catch (err) {
      console.error(err);
    }
  };
  const handleDeleteEvent = (leadershipCouncilId: string) => {
    setLeadershipCouncilToDelete(leadershipCouncilId);
    setShowDeleteModal(true);
  };

  return (
    <>
      <AddPresentation
        show={showAddLeadershipCouncil}
        handleClose={() => {
          setShowAddLeadershipCouncil(false);
          setLeadershipCouncilToEdit(null);
          getMediaTags(searchRef.current);
        }}
        data={leadershipCouncilToEdit}
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (leadershipCouncilToDelete) {
            WebService.deleteAPI({
              action: `resources/delete-resource/${leadershipCouncilToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              getMediaTags(searchRef.current);
            });
          }
        }}
        message="Are you sure you want to delete this industry report?"
      />
      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Presentations</div>
            {pagePermission.canAdd && (
              <CustomButton
                isLoading={false}
                label="Add"
                onClick={() => {
                  setLeadershipCouncilToEdit(null);
                  setShowAddLeadershipCouncil(true);
                }}
                style={{ width: 100 }}
              />
            )}
          </div>
          <div className="row">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title or description"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  getMediaTags(searchRef.current);
                }}
              />
            </div>
          </div>
          <CustomTableSortable
            columns={[
              {
                id: "title",
                label: "Title",
                minWidth: 170,
                sortable: true,
              },
              {
                id: "description",
                label: "Description",
                minWidth: 170,
                sortable: true,
              },
              {
                id: "favourite",
                label: "Favourite",
                minWidth: 80,
                align: "center",
              },
              {
                id: "action",
                label: "Action",
                minWidth: 100,
                align: "center",
              },
            ]}
            rows={leadershipCouncils}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default Presentation;
